import React, { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { GroupLabelFormData } from "./validation.schema";
import { GroupLabelStatusEnum } from "@/domain/grouplabel.interface";

export interface GroupLabelModalPresenterProps {
  open: boolean;
  mode: "create" | "edit";
  isSubmitting: boolean;
  isLoadingGroupLabelData: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export const GroupLabelModalPresenter: FC<GroupLabelModalPresenterProps> = ({
  open,
  mode,
  isSubmitting,
  isLoadingGroupLabelData,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation("common");

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useFormContext<GroupLabelFormData>();

  const title = mode === "create" ? "Create Group Label" : "Edit Group Label";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="headlineSmall">{title}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoadingGroupLabelData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 1,
            }}
          >
            {/* Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  required
                />
              )}
            />

            {/* Description */}
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  required
                />
              )}
            />

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Status"
                  fullWidth
                  error={!!errors.status}
                  helperText={errors.status?.message}
                  required
                >
                  {Object.values(GroupLabelStatusEnum).map((status) => (
                    <MenuItem key={status} value={status}>
                      {t(`enums.${status}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "flex-end",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={16} />}
          >
            {mode === "create" ? "Create" : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
